import { create } from 'zustand';

import {
  feimaBubbleReadConversation,
  feimaBubbleReadSquare,
  feimaBubbleStatus,
  type BubbleStatusResult,
} from '@/api/bubbleApi';
import { getApiUrl } from '@/config/appConfig';
import getDeviceId from '@/utils/deviceFingerprint';
import { getStorageUser } from '@/utils/storage';

import { useWebSocketStore, webSocketClient } from './websocket';

interface BubbleWebSocketMessage {
  type?: string;
  data?: BubbleStatusResult;
}

interface BubbleStoreState {
  convUnread: boolean;
  squareUnread: boolean;
  unreadBotIds: string[];
  squareUnreadBotIds: string[];
  loading: boolean;
  applyBubbleStatus: (data?: BubbleStatusResult) => void;
  connectBubbleWebSocket: (token?: string) => void;
  hasConversationUnread: (botId?: string) => boolean;
  hasSquareUnread: (botId?: string) => boolean;
  loadBubbleStatus: () => Promise<void>;
  markConversationRead: (botId?: string) => Promise<void>;
  markSquareRead: (botId?: string) => Promise<void>;
  resetBubbleStatus: () => void;
}

const uniqStrings = (list?: unknown[]) =>
  Array.from(new Set((list || []).filter(Boolean).map(String)));

const removeBotId = (list: string[], botId: string) =>
  list.filter(item => item !== botId);

const getBubbleWebSocketUrl = (token: string) => {
  const url = new URL(getApiUrl('scrm', '/ws/bubble'), window.location.origin);
  url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
  url.searchParams.set('token', token);
  url.searchParams.set('platform', 'web');
  url.searchParams.set('did', getDeviceId());
  return url.toString();
};

let bubbleMessageOff: (() => void) | null = null;
let connectedToken = '';

const ensureBubbleMessageListener = () => {
  if (bubbleMessageOff) return;

  bubbleMessageOff = webSocketClient.on('message', message => {
    const current = message as BubbleWebSocketMessage;
    if (current?.type !== 'BUBBLE_UPDATE') return;

    useBubbleStore.getState().applyBubbleStatus(current.data);
  });
};

export const useBubbleStore = create<BubbleStoreState>((set, get) => ({
  convUnread: false,
  squareUnread: false,
  unreadBotIds: [],
  squareUnreadBotIds: [],
  loading: false,

  applyBubbleStatus(data) {
    const unreadBotIds = uniqStrings(data?.unreadBotIds);
    const squareUnreadBotIds = uniqStrings(data?.squareUnreadBotIds);

    set({
      convUnread: data?.convUnread ?? unreadBotIds.length > 0,
      squareUnread: data?.squareUnread ?? squareUnreadBotIds.length > 0,
      unreadBotIds,
      squareUnreadBotIds,
    });
  },

  async loadBubbleStatus() {
    set({ loading: true });
    try {
      const res = await feimaBubbleStatus();
      get().applyBubbleStatus(res);
    } finally {
      set({ loading: false });
    }
  },

  connectBubbleWebSocket(token) {
    const currentToken = token || getStorageUser()?.token || '';
    if (!currentToken) {
      get().resetBubbleStatus();
      return;
    }

    ensureBubbleMessageListener();
    const wsStore = useWebSocketStore.getState();

    wsStore.configure({
      getUrl: () => getBubbleWebSocketUrl(currentToken),
      heartbeatMessage: { type: 'ping' },
      reconnect: true,
      queueMessages: true,
    });

    if (connectedToken && connectedToken !== currentToken) {
      connectedToken = currentToken;
      wsStore.reconnect();
      return;
    }

    connectedToken = currentToken;
    if (!['open', 'connecting', 'reconnecting'].includes(wsStore.status)) {
      void wsStore.connect();
    }
  },

  hasConversationUnread(botId) {
    return Boolean(botId && get().unreadBotIds.includes(botId));
  },

  hasSquareUnread(botId) {
    return Boolean(botId && get().squareUnreadBotIds.includes(botId));
  },

  async markConversationRead(botId) {
    if (!botId || !get().unreadBotIds.includes(botId)) return;

    await feimaBubbleReadConversation({ botId });
    set(state => {
      const unreadBotIds = removeBotId(state.unreadBotIds, botId);
      return {
        unreadBotIds,
        convUnread: unreadBotIds.length > 0,
      };
    });
  },

  async markSquareRead(botId) {
    if (!botId || !get().squareUnreadBotIds.includes(botId)) return;

    await feimaBubbleReadSquare({ botId });
    set(state => {
      const squareUnreadBotIds = removeBotId(state.squareUnreadBotIds, botId);
      return {
        squareUnreadBotIds,
        squareUnread: squareUnreadBotIds.length > 0,
      };
    });
  },

  resetBubbleStatus() {
    connectedToken = '';
    useWebSocketStore.getState().close(1000, 'bubble reset');
    set({
      convUnread: false,
      squareUnread: false,
      unreadBotIds: [],
      squareUnreadBotIds: [],
      loading: false,
    });
  },
}));
